import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input, Textarea } from "../ui/input";
import { ImageUpload } from "../subcomponents/ImageUpload";

type StaffFormState = {
  name: string;
  role: string;
  image: string;
  isOrganizer: boolean;
  emails: string;
  phones: string;
  bibliography: string;
  researchInterests: string;
  publications: string;
  cv: string;
};

const EMPTY_STAFF: StaffFormState = {
  name: "",
  role: "",
  image: "",
  isOrganizer: false,
  emails: "",
  phones: "",
  bibliography: "",
  researchInterests: "",
  publications: "",
  cv: "",
};

type Props = {
  open: boolean;
  editing: any | null;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "staff-form";

const toLines = (s: string): string[] =>
  s.split("\n").map((x) => x.trim()).filter(Boolean);

const fromLines = (a?: string[] | null): string =>
  Array.isArray(a) ? a.join("\n") : "";

export const AdminStaffModal: React.FC<Props> = ({ open, editing, onClose, onSaved }) => {
  const { t } = useLanguage();

  const schema = Yup.object({
    name: Yup.string().trim().required(t.common.required),
    role: Yup.string().trim().required(t.common.required),
    image: Yup.string(),
    isOrganizer: Yup.boolean(),
    emails: Yup.string(),
    phones: Yup.string(),
    bibliography: Yup.string(),
    researchInterests: Yup.string(),
    publications: Yup.string(),
    cv: Yup.string(),
  });

  const initial: StaffFormState = editing
    ? {
        name: editing.name || "",
        role: editing.role || "",
        image: editing.image || "",
        isOrganizer: !!editing.isOrganizer,
        emails: fromLines(editing.email),
        phones: fromLines(editing.phone),
        bibliography: editing.extra?.bibliography || "",
        researchInterests: fromLines(editing.extra?.researchInterests),
        publications: fromLines(editing.extra?.publications),
        cv: editing.extra?.cv || "",
      }
    : EMPTY_STAFF;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const researchInterests = toLines(values.researchInterests);
        const publications = toLines(values.publications);
        const extra: Record<string, any> = {};
        if (values.bibliography.trim()) extra.bibliography = values.bibliography.trim();
        if (researchInterests.length) extra.researchInterests = researchInterests;
        if (publications.length) extra.publications = publications;
        if (values.cv.trim()) extra.cv = values.cv.trim();

        const payload = {
          name: values.name.trim(),
          role: values.role.trim(),
          image: values.image || null,
          isOrganizer: values.isOrganizer,
          email: toLines(values.emails),
          phone: toLines(values.phones),
          extra: Object.keys(extra).length ? extra : null,
        };
        try {
          if (editing) await apiPut(`/staff/${editing.id}`, payload);
          else await apiPost("/staff", payload);
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? t.admin.editStaff : t.admin.newStaff}
          subtitle={t.admin.staffSubtitle}
          testId="staff-modal"
          className="max-w-2xl"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdminField label={t.admin.name} required error={touched.name ? errors.name : undefined}>
                <Field as={Input} name="name" data-testid="staff-name" />
              </AdminField>
              <AdminField label={t.admin.role} required error={touched.role ? errors.role : undefined}>
                <Field as={Input} name="role" data-testid="staff-role" />
              </AdminField>
            </div>

            <ImageUpload
              value={values.image}
              onChange={(url) => setFieldValue("image", url)}
              folder="ulpgl/avatars"
              label={t.admin.staffImage}
              testId="staff-image"
            />

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                data-testid="staff-organizer"
                checked={values.isOrganizer}
                onChange={(e) => setFieldValue("isOrganizer", e.target.checked)}
              />
              {t.admin.staffIsOrganizer}
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdminField label={t.admin.staffEmails}>
                <Field as={Textarea} name="emails" rows={2} placeholder={t.admin.staffOnePerLine} data-testid="staff-emails" />
              </AdminField>
              <AdminField label={t.admin.staffPhones}>
                <Field as={Textarea} name="phones" rows={2} placeholder={t.admin.staffOnePerLine} data-testid="staff-phones" />
              </AdminField>
            </div>

            <AdminField label={t.admin.staffBibliography}>
              <Field as={Textarea} name="bibliography" rows={3} data-testid="staff-bibliography" />
            </AdminField>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <AdminField label={t.admin.staffResearchInterests}>
                <Field as={Textarea} name="researchInterests" rows={3} placeholder={t.admin.staffOnePerLine} />
              </AdminField>
              <AdminField label={t.admin.staffPublications}>
                <Field as={Textarea} name="publications" rows={3} placeholder={t.admin.staffOnePerLine} />
              </AdminField>
            </div>

            <AdminField label={t.admin.staffCv}>
              <Field as={Input} name="cv" placeholder="https://" />
            </AdminField>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
